import React, { useEffect } from 'react';
import moment from 'moment';
import styles from './styles.module.scss';
import { useStore } from '../../../../../../../stores/root';

// assets
import { faCheck, faXmarkCircle } from '@fortawesome/pro-light-svg-icons';

// services
import GeneralService from '../../../../../../../services/general';
import ErrorService from '../../../../../../../services/general/error';
import IncomeInvoiceService from '../../../../../../../services/finance/incomes/invoices/invoice';

// props
import { IIncomePaymentResourceShortProps } from '../../../../../../../props/finance/incomes/payments/payment';

// components
import { ActionButton, CommandBar, IColumn, ICommandBarItemProps, Link, PrimaryButton, SearchBox, SelectionMode, ShimmeredDetailsList, Stack } from '@fluentui/react';
import Text from './../../../../../../typography/text';
import PaymentMethods from '../../../../../../../manifests/paymentMethods';
import PermissionsService from '../../../../../../../services/permissions';
import { observer } from 'mobx-react';
import IncomePaymentService from '../../../../../../../services/finance/incomes/payments/payment';
import Tag, { TTagVariant } from '../../../../../../uiframeworks/tag';
import { IIncomeInvoicePartResourceShortProps } from '../../../../../../../props/finance/incomes/invoices/part';
import IncomeInvoicePartService from '../../../../../../../services/finance/incomes/invoices/part';
import LoadingDialogComponent from '../../../../../../feedbacks/loadingDialog';
import PaymentsStatus from '../../../../../../../manifests/status/payment';
import { IIncomeInvoiceResourceProps } from '../../../../../../../props/finance/incomes/invoices/invoice';

interface IncomeInvoicePartsListProps {
    invoiceId?: string;
    qs?: string[];
    parts?: IIncomeInvoicePartResourceShortProps[];
    columns?: TIncomeInvoicePartColumn[];
    hideSearch?: boolean;
    hideCommandBar?: boolean;
    hideCreateButton?: boolean;
    variant?: 'card' | 'plain';
}

export type TIncomeInvoicePartColumn = 'key' | 'amount' | 'notes' | 'dueDate' | 'paidOn' | 'status' | 'actions' | 'unpaidAmount' | 'paymentStatus';

const IncomeInvoicePartsList: React.FC<IncomeInvoicePartsListProps> = observer((props: IncomeInvoicePartsListProps) => {
    const shownColumns: TIncomeInvoicePartColumn[] = props.columns || [
        'key',
        'amount',
        'notes',
        'dueDate',
        'paidOn',
        'status',
        'paymentStatus',
        'unpaidAmount',
        'actions'
    ];

    const { banner, user } = useStore();
    const [loaded, setLoaded] = React.useState<boolean>(false);
    const [invoice, setInvoice] = React.useState<IIncomeInvoiceResourceProps | undefined>();
    const [parts, setParts] = React.useState<IIncomeInvoicePartResourceShortProps[]>(props.parts || []);
    const [activePart, setActivePart] = React.useState<IIncomeInvoicePartResourceShortProps | undefined>();
    const [keyword, setKeyword] = React.useState<string>("");
    const [activeSurface, setActiveSurface] = React.useState<string | undefined>();
    const [downloading, setDownloading] = React.useState<boolean>(false);

    const columns: IColumn[] = [
        {
            key: "key",
            name: "Part #",
            minWidth: 225,
            maxWidth: 225,
            onRender: (item: IIncomeInvoicePartResourceShortProps) => {
                return <Stack styles={{ root: { padding: '6px 0px' } }}>
                    <Text size={'small'}>{item.invoiceId}-{item.key}</Text>
                </Stack>
            }
        },
        {
            key: "amount",
            name: "Amount",
            minWidth: 150,
            maxWidth: 150,
            onRender: (item: IIncomeInvoicePartResourceShortProps) => {
                let text: string = "";
                if (invoice) {
                    const percentage = Number((Number(item.amount) / Number(invoice?.totalAmount) * 100).toFixed(2));
                    text = `Rp. ${GeneralService.getNumberWithSeparator(item.amount)} (${GeneralService.getNumberWithSeparator(percentage)}%)`;
                } else {
                    text = `Rp. ${GeneralService.getNumberWithSeparator(item.amount)}`
                }

                return <Stack styles={{ root: { padding: '6px 0px' } }}>
                    <Text size={'small'}>{text}</Text>
                </Stack>
            }
        },
        {
            key: "dueDate",
            name: "Due Date",
            minWidth: 150,
            maxWidth: 150,
            onRender: (item: IIncomeInvoicePartResourceShortProps) => {
                return <Stack styles={{ root: { padding: '6px 0px' } }}>
                    <Text size={'small'}>{GeneralService.getFriendlyDateFormat(item.dueDate)}</Text>
                </Stack>
            }
        },
        {
            key: "notes",
            name: "Notes",
            minWidth: 100,
            isMultiline: true,
            onRender: (item: IIncomeInvoicePartResourceShortProps) => {
                return <Stack styles={{ root: { padding: '6px 0px' } }}>
                    <Text size={'small'}>{item.notes}</Text>
                </Stack>
            }
        },
        {
            key: "unpaidAmount",
            name: "Unpaid Amount",
            minWidth: 150,
            maxWidth: 150,
            onRender: (item: IIncomeInvoicePartResourceShortProps) => {
                return <Stack styles={{ root: { padding: '6px 0px' } }}>
                    <Text className={'color-red'} size={'small'}>Rp. {GeneralService.getNumberWithSeparator(item.unpaid)}</Text>
                </Stack>
            }
        },
        {
            key: "paymentStatus",
            name: "Payment Status",
            minWidth: 100,
            maxWidth: 100,
            onRender: (item: IIncomeInvoicePartResourceShortProps) => {
                const status = PaymentsStatus.find((s) => s.key === item.paymentStatus);
                return <Stack horizontal styles={{ root: { padding: '4px 0px' } }}>
                    <Tag text={status?.text} variant={status?.tagVariant} size={'small'} />
                </Stack>
            }
        },
        {
            key: "actions",
            name: "",
            minWidth: 125,
            maxWidth: 125,
            onRender: (item: IIncomeInvoicePartResourceShortProps) => {
                const hasDeletePermission = PermissionsService.hasPermission(['incomes.invoices.delete', 'incomes.invoices.update.all'], user.permissions);

                return <Stack.Item styles={{ root: { padding: '0px' } }}>
                    <ActionButton text={'Download'} iconProps={{ iconName: "Download" }} onClick={() => {
                        _onDownloadInvoice(item, 'docx');
                    }} />
                    {hasDeletePermission ? <ActionButton iconProps={{ iconName: "Delete" }} onClick={() => {
                        setActivePart(item);
                        setActiveSurface('delete');
                    }} /> : null}
                </Stack.Item>;
            }
        }
    ];

    const _onDownloadInvoice = async (part: IIncomeInvoicePartResourceShortProps, format: string) => {
        try {
            setDownloading(true);
            const name = IncomeInvoiceService.getInvoiceNumber(part.invoiceId) + "-" + part.key;
            await IncomeInvoicePartService.download(part.invoiceId, part.id, format, name);

            banner.add({
                key: 'download_invoice_success',
                text: `Part invoice "${name}" downloaded successfully`,
                icon: faCheck,
                variant: 'success'
            });
            setDownloading(false);
        } catch (e) {
            setDownloading(false);
            banner.add({
                key: 'download_invoice_error',
                text: 'Failed to download invoice. Error: ' + ErrorService.getMessage(e),
                icon: faXmarkCircle,
                variant: 'error'
            });
        }
    }

    useEffect(() => {
        if (!props.parts) {
            _onRetrieveParts();
        } else {
            init();
        }
    }, []);

    const init = async () => {
        if (!invoice && props.invoiceId) {
            setLoaded(false);

            const _invoice = await IncomeInvoiceService.get(props.invoiceId);
            setInvoice(_invoice);

            setLoaded(true);
        }

        setLoaded(true);
    }

    const _onRetrieveParts = async (pageNumber?: number,) => {
        try {
            if (props.invoiceId) {
                if (!pageNumber) {
                    setLoaded(false);
                }

                if (!invoice) {
                    const _invoice = await IncomeInvoiceService.get(props.invoiceId);
                    setInvoice(_invoice);
                }

                const qs: string[] = props.qs || [];
                qs.push(`page=all`)
                if (keyword && keyword.trim() !== "") { qs.push(`search=${keyword}`) }

                const result = await IncomeInvoicePartService.retrieve(props.invoiceId, qs.join("&"));
                setParts(result.data);
                setLoaded(true);
            }
        } catch (e) {
            banner.add({
                key: 'retrieve_invoice_parts_error',
                text: 'Failed to retrieve invoice parts list. Error: ' + ErrorService.getMessage(e),
                icon: faXmarkCircle,
                variant: 'error'
            });
        }
    }

    const _onKeywordChanged = (value?: string) => {
        setLoaded(true);
        setKeyword(value || "");
    }

    const getCommandBarItems = () => {
        let items: ICommandBarItemProps[] = [];
        let farItems: ICommandBarItemProps[] = [];

        if (!props.hideSearch) {
            items.push({
                key: "search",
                onRender: () => {
                    return <SearchBox placeholder={"Search ..."} onSearch={_onKeywordChanged} />
                }
            });
        }

        return { items, farItems };
    }


    const _onSurfaceDismissed = (refresh?: boolean) => {
        setActiveSurface(undefined);
        setActivePart(undefined);

        if (refresh) { _onRetrieveParts() }
    }

    return <Stack className={styles.container} tokens={{ childrenGap: 20 }}>
        <Stack className={styles[props.variant || 'card']}>
            {
                !loaded || (loaded && parts.length > 0) ? (
                    <>
                        <ShimmeredDetailsList
                            setKey="items"
                            items={parts}
                            columns={columns.filter((col) => shownColumns.indexOf(col.key as TIncomeInvoicePartColumn) > -1)}
                            selectionMode={SelectionMode.none}
                            enableShimmer={!loaded}
                            onShouldVirtualize={() => false}
                            ariaLabelForShimmer="Content is being fetched"
                            ariaLabelForGrid="Item details" />
                    </>
                ) : null
            }
            {downloading ? <LoadingDialogComponent title='Downloading Invoice' secondaryText="Please wait. Your invoice will be downloaded momentarily." /> : null}
            {loaded && parts.length < 1 ? <Stack styles={{ root: { padding: props.variant === 'plain' ? 0 : 10 } }}>
                <Text size={'small'} style={{ fontStyle: 'italic' }}>Parts not found</Text>
            </Stack> : null}
        </Stack>
        {/*activeSurface === 'delete' && activePayment ? <DeleteOrderInvoicePaymentForm payment={activePayment} onDismissed={_onSurfaceDismissed} /> : null*/}
    </Stack>;
});

export default IncomeInvoicePartsList;
